(function () {
  const elementName = 'hym-pdf-flipbook';

  if (window.customElements.get(elementName)) {
    return;
  }

  const waitForLibraries = () =>
    new Promise((resolve, reject) => {
      let attempts = 0;
      const timer = window.setInterval(() => {
        attempts += 1;

        if (window.pdfjsLib && window.St && window.St.PageFlip) {
          window.clearInterval(timer);
          resolve();
          return;
        }

        if (attempts > 100) {
          window.clearInterval(timer);
          reject(new Error('Catalog libraries were not loaded.'));
        }
      }, 50);
    });

  class HymPdfFlipbook extends HTMLElement {
    connectedCallback() {
      if (this.isReady) return;

      this.isReady = true;
      this.pdfUrl = (this.dataset.pdfUrl || '').trim();
      this.workerSrc = this.dataset.workerSrc;
      this.renderScale = Number(this.dataset.renderScale) || 1.9;
      this.maxWidth = Number(this.dataset.maxWidth) || 1180;
      this.showCover = this.dataset.showCover === 'true';
      this.startPage = Math.max(0, (Number(this.dataset.startPage) || 1) - 1);
      this.renderedPages = new Set();
      this.renderingPages = new Map();
      this.pageElements = [];

      this.viewer = this.querySelector('[data-hym-viewer]');
      this.stage = this.querySelector('[data-hym-stage]');
      this.book = this.querySelector('[data-hym-book]');
      this.bookWrap = this.querySelector('[data-hym-wrap]');
      this.empty = this.querySelector('[data-hym-empty]');
      this.loading = this.querySelector('[data-hym-loading]');
      this.status = this.querySelector('[data-hym-status]');
      this.error = this.querySelector('[data-hym-error]');
      this.toolbar = this.querySelector('[data-hym-toolbar]');
      this.currentPage = this.querySelector('[data-hym-current]');
      this.totalPages = this.querySelector('[data-hym-total]');
      this.zoomLabel = this.querySelector('[data-hym-zoom-label]');
      this.fullscreenToggle = this.querySelector('[data-hym-fullscreen-toggle]');
      this.zoom = 1;
      this.minZoom = 1;
      this.maxZoom = 2;
      this.zoomStep = 0.25;
      this.baseBookMaxWidth = '';
      this.touchSurface = null;
      this.touchState = null;
      this.zoomUpdateTimer = null;

      this.onToolbarClick = this.onToolbarClick.bind(this);
      this.onFullscreenChange = this.onFullscreenChange.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
      this.onBookTouchStart = this.onBookTouchStart.bind(this);
      this.onBookTouchMove = this.onBookTouchMove.bind(this);
      this.onBookTouchEnd = this.onBookTouchEnd.bind(this);
      this.headerOffsetFrame = null;
      this.stageSizeFrame = null;

      if (!this.pdfUrl) {
        this.showEmpty();
        return;
      }

      this.init();
    }

    disconnectedCallback() {
      document.removeEventListener('fullscreenchange', this.onFullscreenChange);
      this.unbindTouchPageControls();

      if (this.viewer) {
        this.viewer.removeEventListener('click', this.onToolbarClick);
      }

      window.removeEventListener('scroll', this.onViewportChange);
      window.removeEventListener('resize', this.onViewportChange);

      if (this.headerOffsetFrame) {
        window.cancelAnimationFrame(this.headerOffsetFrame);
      }

      if (this.stageSizeFrame) {
        window.cancelAnimationFrame(this.stageSizeFrame);
      }

      if (this.zoomUpdateTimer) {
        window.clearTimeout(this.zoomUpdateTimer);
      }

      if (this.pageFlip) {
        try {
          this.pageFlip.destroy();
        } catch (error) {
          // The library can throw if Shopify removes the section mid-animation.
        }
      }
    }

    async init() {
      try {
        await waitForLibraries();

        window.pdfjsLib.GlobalWorkerOptions.workerSrc = this.workerSrc;

        const loadingTask = window.pdfjsLib.getDocument({
          url: this.pdfUrl,
          disableRange: false,
          disableStream: false,
          disableAutoFetch: false
        });

        loadingTask.onProgress = (progress) => {
          if (!this.status || !progress.total) return;

          const percent = Math.round((progress.loaded / progress.total) * 100);
          this.status.textContent = `Cargando catálogo... ${percent}%`;
        };

        this.pdf = await loadingTask.promise;
        const firstPage = await this.pdf.getPage(1);
        const firstViewport = firstPage.getViewport({ scale: 1 });

        this.pageWidth = Math.round(firstViewport.width);
        this.pageHeight = Math.round(firstViewport.height);
        this.startPage = Math.min(this.startPage, this.pdf.numPages - 1);

        this.createPages();
        this.mountFlipbook();
        this.bindControls();
        this.renderInitialPages(this.startPage);
      } catch (error) {
        this.showError(error);
      }
    }

    createPages() {
      this.book.innerHTML = '';
      this.pageElements = [];

      for (let index = 0; index < this.pdf.numPages; index += 1) {
        const page = document.createElement('div');
        page.className = 'hym-catalog-page';
        page.dataset.hymPage = String(index + 1);

        if (this.showCover && (index === 0 || index === this.pdf.numPages - 1)) {
          page.dataset.density = 'hard';
        }

        page.appendChild(this.createPlaceholder(index + 1));
        this.book.appendChild(page);
        this.pageElements.push(page);
      }
    }

    mountFlipbook() {
      this.hide(this.empty);
      this.hide(this.loading);
      this.hide(this.error);
      this.show(this.bookWrap);
      this.show(this.toolbar);
      this.show(this.fullscreenToggle);
      this.viewer.dataset.zoomed = 'false';
      this.viewer.dataset.touchMode = this.shouldUseNaturalSwipe() ? 'true' : 'false';

      this.pageFlip = new window.St.PageFlip(this.book, {
        width: this.pageWidth,
        height: this.pageHeight,
        size: 'stretch',
        minWidth: 260,
        maxWidth: Math.max(390, Math.round(this.maxWidth / 2)),
        minHeight: 350,
        maxHeight: 1200,
        autoSize: true,
        drawShadow: true,
        flippingTime: 720,
        usePortrait: true,
        startPage: this.startPage,
        showCover: this.showCover,
        maxShadowOpacity: 0.42,
        mobileScrollSupport: true,
        useMouseEvents: true,
        swipeDistance: 30
      });

      this.pageFlip.loadFromHTML(this.pageElements);
      this.patchPageFlipCanvasCopies();
      this.patchPortraitPreviousFlipDrawing();
      this.bindTouchPageControls();
      this.baseBookMaxWidth = this.book.style.maxWidth || '';
      this.pageFlip.on('flip', (event) => {
        const pageIndex = Number(event.data) || 0;
        this.updateControls(pageIndex);
        this.renderAround(pageIndex);
        this.scheduleMobileStageSize();
      });
      this.pageFlip.on('changeOrientation', () => {
        const pageIndex = this.pageFlip.getCurrentPageIndex();
        this.updateControls(pageIndex);
        this.renderAround(pageIndex);
        this.scheduleMobileStageSize();
      });

      this.updateControls(this.startPage);
      this.updateZoomControls();
      this.updateHeaderOffset();
      this.scheduleMobileStageSize();
    }

    bindControls() {
      this.viewer.addEventListener('click', this.onToolbarClick);
      document.addEventListener('fullscreenchange', this.onFullscreenChange);
      window.addEventListener('scroll', this.onViewportChange, { passive: true });
      window.addEventListener('resize', this.onViewportChange);
    }

    patchPageFlipCanvasCopies() {
      if (!this.pageFlip || !this.pageFlip.getPageCollection) return;

      const pageCollection = this.pageFlip.getPageCollection();

      if (!pageCollection || pageCollection.hymCanvasCopyPatched) return;

      const pages = pageCollection.getPages ? pageCollection.getPages() : pageCollection.pages;

      if (Array.isArray(pages)) {
        pages.forEach((pageObject) => this.patchTemporaryPageCopy(pageObject));
      }

      if (pageCollection.getFlippingPage) {
        const getFlippingPage = pageCollection.getFlippingPage.bind(pageCollection);

        pageCollection.getFlippingPage = function getHymFlippingPage(direction) {
          const isPortrait =
            this.render &&
            this.render.getOrientation &&
            this.render.getOrientation() === 'portrait';
          const previousPage = this.pages && this.pages[this.currentSpreadIndex - 1];

          if (
            direction === 1 &&
            isPortrait &&
            previousPage &&
            previousPage.newTemporaryCopy
          ) {
            return previousPage.newTemporaryCopy();
          }

          return getFlippingPage(direction);
        };
      }

      pageCollection.hymCanvasCopyPatched = true;
    }

    patchPortraitPreviousFlipDrawing() {
      if (!this.pageFlip || !this.pageFlip.getRender) return;

      const render = this.pageFlip.getRender();

      if (!render || render.hymPortraitPreviousDrawingPatched || !render.drawFrame) return;

      const drawFrame = render.drawFrame.bind(render);

      render.drawFrame = () => {
        drawFrame();
        this.shiftPortraitPreviousFlipFrame(render);
      };
      render.hymPortraitPreviousDrawingPatched = true;
    }

    shiftPortraitPreviousFlipFrame(render) {
      const isPortrait =
        render.getOrientation &&
        render.getOrientation() === 'portrait';
      const isPrevious =
        render.getDirection &&
        render.getDirection() === 1;

      if (!isPortrait || !isPrevious || !render.getRect) return;

      const bounds = render.getRect();
      const controller =
        this.pageFlip &&
        this.pageFlip.getFlipController &&
        this.pageFlip.getFlipController();

      if (!bounds || !bounds.pageWidth || !controller) return;

      const shiftElement = (element) => {
        if (!element || !element.style || !element.style.transform) return;

        element.style.transform = this.shiftTransformX(
          element.style.transform,
          bounds.pageWidth
        );
      };

      if (controller.flippingPage && controller.flippingPage.getElement) {
        shiftElement(controller.flippingPage.getElement());
      }

      shiftElement(render.innerShadow);
      shiftElement(render.outerShadow);
    }

    shiftTransformX(transform, amount) {
      const shiftTranslate = (match, x, y, suffix) =>
        `translate3d(${Number(x) + amount}px, ${y}px, 0${suffix || ''})`;

      if (transform.includes('translate3d(')) {
        return transform.replace(
          /translate3d\(([-0-9.]+)px,\s*([-0-9.]+)px,\s*0(px)?\)/,
          shiftTranslate
        );
      }

      return transform.replace(
        /translate\(([-0-9.]+)px,\s*([-0-9.]+)px\)/,
        (match, x, y) => `translate(${Number(x) + amount}px, ${y}px)`
      );
    }

    patchTemporaryPageCopy(pageObject) {
      if (!pageObject || pageObject.hymCanvasCopyPatched || !pageObject.newTemporaryCopy) return;

      const newTemporaryCopy = pageObject.newTemporaryCopy.bind(pageObject);

      pageObject.newTemporaryCopy = () => {
        const temporaryCopy = newTemporaryCopy();
        this.copyTemporaryPageCanvases(pageObject);
        return temporaryCopy;
      };
      pageObject.hymCanvasCopyPatched = true;
    }

    copyTemporaryPageCanvases(pageObject) {
      const sourcePage = pageObject && pageObject.element;
      const copiedPage = pageObject && pageObject.copiedElement;

      if (!sourcePage || !copiedPage) return;

      const sourceCanvases = sourcePage.querySelectorAll('canvas');
      const copiedCanvases = copiedPage.querySelectorAll('canvas');

      sourceCanvases.forEach((sourceCanvas, index) => {
        const copiedCanvas = copiedCanvases[index];

        if (!copiedCanvas || !sourceCanvas.width || !sourceCanvas.height) return;

        copiedCanvas.width = sourceCanvas.width;
        copiedCanvas.height = sourceCanvas.height;

        const context = copiedCanvas.getContext('2d', { alpha: false });

        if (!context) return;

        context.drawImage(sourceCanvas, 0, 0);
      });
    }

    bindTouchPageControls() {
      this.unbindTouchPageControls();

      if (!this.pageFlip || !this.pageFlip.getUI) return;

      const ui = this.pageFlip.getUI();
      this.touchSurface = ui && ui.getDistElement ? ui.getDistElement() : null;

      if (!this.touchSurface) return;

      this.touchSurface.addEventListener('touchstart', this.onBookTouchStart, {
        capture: true,
        passive: false
      });
      window.addEventListener('touchmove', this.onBookTouchMove, {
        capture: true,
        passive: false
      });
      window.addEventListener('touchend', this.onBookTouchEnd, {
        capture: true,
        passive: false
      });
      window.addEventListener('touchcancel', this.onBookTouchEnd, {
        capture: true,
        passive: false
      });
    }

    unbindTouchPageControls() {
      if (this.touchSurface) {
        this.touchSurface.removeEventListener('touchstart', this.onBookTouchStart, true);
      }

      window.removeEventListener('touchmove', this.onBookTouchMove, true);
      window.removeEventListener('touchend', this.onBookTouchEnd, true);
      window.removeEventListener('touchcancel', this.onBookTouchEnd, true);

      this.touchSurface = null;
      this.touchState = null;
    }

    onBookTouchStart(event) {
      if (!this.pageFlip) return;

      const target = event.target instanceof Element ? event.target : null;

      if (target && target.closest('a, button, input, select, textarea')) return;

      if (event.touches.length === 2) {
        this.startZoomPinch(event);
        return;
      }

      if (event.touches.length !== 1) return;

      if (this.isZoomed()) {
        this.startZoomPan(event);
        return;
      }

      const touch = event.changedTouches[0];
      const point = this.getTouchPoint(touch);

      if (!point) return;

      event.stopImmediatePropagation();

      this.touchState = {
        id: touch.identifier,
        startX: point.x,
        startY: point.y,
        lastX: point.x,
        lastY: point.y,
        startTime: Date.now(),
        dragging: false,
        direction: null
      };
    }

    onBookTouchMove(event) {
      if (!this.touchState || !this.pageFlip) return;

      if (this.touchState.mode === 'zoom-pan') {
        this.moveZoomPan(event);
        return;
      }

      if (this.touchState.mode === 'pinch') {
        this.moveZoomPinch(event);
        return;
      }

      const touch = this.getTrackedTouch(event.touches) || this.getTrackedTouch(event.changedTouches);
      const point = touch ? this.getTouchPoint(touch) : null;

      if (!point) return;

      const deltaX = point.x - this.touchState.startX;
      const deltaY = point.y - this.touchState.startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      this.touchState.lastX = point.x;
      this.touchState.lastY = point.y;

      if (!this.touchState.dragging) {
        if (absY > 14 && absY > absX * 1.2) {
          this.cancelTouchTracking();
          return;
        }

        if (absX < 12 || absX < absY) return;

        const direction = deltaX < 0 ? 'next' : 'prev';

        if (!this.isTouchFlipAvailable(direction)) {
          this.cancelTouchTracking();
          return;
        }

        this.touchState.dragging = true;
        this.touchState.direction = direction;
        this.touchState.dragAnchor = this.startTouchPageDrag(direction, point.y);
      }

      const dragPoint = this.getTouchDragPoint(point);

      this.stopTouchEvent(event, true);
      this.pageFlip.userMove(dragPoint, true);
    }

    onBookTouchEnd(event) {
      if (!this.touchState || !this.pageFlip) return;

      if (this.touchState.mode === 'zoom-pan' || this.touchState.mode === 'pinch') {
        this.finishZoomTouch(event);
        return;
      }

      const touch = this.getTrackedTouch(event.changedTouches);
      const point = touch ? this.getTouchPoint(touch) : null;
      const lastPoint = point || {
        x: this.touchState.lastX,
        y: this.touchState.lastY
      };
      const deltaX = lastPoint.x - this.touchState.startX;
      const deltaY = lastPoint.y - this.touchState.startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      const elapsed = Date.now() - this.touchState.startTime;

      if (this.touchState.dragging) {
        const direction = this.touchState.direction;
        const surfaceWidth = this.touchSurface ? this.touchSurface.clientWidth : 0;
        const distanceThreshold = Math.max(36, Math.min(120, surfaceWidth * 0.16));
        const signedCorrectly =
          (direction === 'next' && deltaX < 0) || (direction === 'prev' && deltaX > 0);
        const isFastSwipe = elapsed < 280 && absX > 28 && absX > absY * 1.15;
        const shouldComplete = signedCorrectly && (absX >= distanceThreshold || isFastSwipe);

        this.stopTouchEvent(event, true);
        this.finishTouchPageDrag(shouldComplete);
        this.touchState = null;
        return;
      }

      if (elapsed < 350 && absX < 10 && absY < 10) {
        const edgeAction = this.getTouchEdgeAction(this.touchState.startX);

        if (edgeAction && this.isTouchFlipAvailable(edgeAction)) {
          this.stopTouchEvent(event, true);

          if (edgeAction === 'next') {
            this.goToNext();
          } else {
            this.goToPrev();
          }
        }
      }

      this.touchState = null;
    }

    startZoomPan(event) {
      if (!this.stage || event.touches.length !== 1) return;

      const touch = event.changedTouches[0] || event.touches[0];

      this.cancelPageFlipTouch();
      this.stopTouchEvent(event, true);

      this.touchState = {
        mode: 'zoom-pan',
        id: touch.identifier,
        lastClientX: touch.clientX,
        lastClientY: touch.clientY
      };
    }

    moveZoomPan(event) {
      if (!this.stage || !this.touchState) return;

      const touch = this.getTrackedTouch(event.touches) || this.getTrackedTouch(event.changedTouches);

      if (!touch) return;

      const deltaX = touch.clientX - this.touchState.lastClientX;
      const deltaY = touch.clientY - this.touchState.lastClientY;

      this.stopTouchEvent(event, true);
      this.stage.scrollLeft -= deltaX;
      this.stage.scrollTop -= deltaY;
      this.touchState.lastClientX = touch.clientX;
      this.touchState.lastClientY = touch.clientY;
    }

    startZoomPinch(event) {
      if (!this.stage || event.touches.length < 2) return;

      const touches = Array.from(event.touches).slice(0, 2);
      const distance = this.getTouchDistance(touches);

      if (distance < 10) return;

      const center = this.getTouchCenter(touches);
      const stageRect = this.stage.getBoundingClientRect();
      const viewportX = center.x - stageRect.left;
      const viewportY = center.y - stageRect.top;

      this.cancelPageFlipTouch();
      this.stopTouchEvent(event, true);
      this.viewer.classList.add('is-touch-zooming');

      this.touchState = {
        mode: 'pinch',
        startDistance: distance,
        startZoom: this.zoom,
        focus: {
          contentX: this.stage.scrollLeft + viewportX,
          contentY: this.stage.scrollTop + viewportY,
          viewportX,
          viewportY,
          zoom: this.zoom
        }
      };
    }

    moveZoomPinch(event) {
      if (!this.touchState || !this.stage || event.touches.length < 2) return;

      const touches = Array.from(event.touches).slice(0, 2);
      const distance = this.getTouchDistance(touches);
      const center = this.getTouchCenter(touches);
      const stageRect = this.stage.getBoundingClientRect();
      const nextZoom = this.touchState.startZoom * (distance / this.touchState.startDistance);

      this.stopTouchEvent(event, true);
      this.setZoom(nextZoom, {
        focus: {
          contentX: this.touchState.focus.contentX,
          contentY: this.touchState.focus.contentY,
          viewportX: center.x - stageRect.left,
          viewportY: center.y - stageRect.top,
          zoom: this.touchState.focus.zoom
        },
        interactive: true
      });
    }

    finishZoomTouch(event) {
      this.stopTouchEvent(event, true);
      this.viewer.classList.remove('is-touch-zooming');

      if (event.touches && event.touches.length === 1 && this.isZoomed()) {
        const touch = event.touches[0];

        this.touchState = {
          mode: 'zoom-pan',
          id: touch.identifier,
          lastClientX: touch.clientX,
          lastClientY: touch.clientY
        };
        return;
      }

      this.touchState = null;
    }

    getTrackedTouch(touchList) {
      if (!this.touchState || !touchList) return null;

      return Array.from(touchList).find((touch) => touch.identifier === this.touchState.id) || null;
    }

    getTouchPoint(touch) {
      if (!this.touchSurface || !touch) return null;

      const rect = this.touchSurface.getBoundingClientRect();

      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      };
    }

    getTouchDistance(touches) {
      const [first, second] = touches;
      const deltaX = second.clientX - first.clientX;
      const deltaY = second.clientY - first.clientY;

      return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    }

    getTouchCenter(touches) {
      const [first, second] = touches;

      return {
        x: (first.clientX + second.clientX) / 2,
        y: (first.clientY + second.clientY) / 2
      };
    }

    startTouchPageDrag(direction, y) {
      const anchor = this.getTouchDragAnchor(direction, y);
      const render = this.pageFlip.getRender && this.pageFlip.getRender();
      const controller = this.pageFlip.getFlipController && this.pageFlip.getFlipController();

      if (render && render.finishAnimation) {
        render.finishAnimation();
      }

      this.pageFlip.startUserTouch(anchor);

      if (controller && controller.fold) {
        controller.fold(anchor);
      }

      return anchor;
    }

    getTouchDragPoint(point) {
      if (!this.touchState || !this.touchState.dragAnchor) return point;

      const isPortrait = this.pageFlip.getOrientation && this.pageFlip.getOrientation() === 'portrait';
      const dragFactor =
        this.touchState.direction === 'prev' && isPortrait
          ? 1.4
          : 1;
      const bounds = this.pageFlip.getBoundsRect();
      const minX = bounds.left + 3;
      const maxX = bounds.left + bounds.width - 3;
      const nextX = this.touchState.dragAnchor.x + (point.x - this.touchState.startX) * dragFactor;

      return {
        x: Math.min(maxX, Math.max(minX, nextX)),
        y: this.touchState.dragAnchor.y + (point.y - this.touchState.startY)
      };
    }

    finishTouchPageDrag(shouldComplete) {
      const controller = this.pageFlip.getFlipController && this.pageFlip.getFlipController();
      const calc = controller && controller.getCalculation ? controller.getCalculation() : null;

      this.pageFlip.isUserTouch = false;
      this.pageFlip.isUserMove = false;

      if (!controller || !calc || !controller.animateFlippingTo) {
        return;
      }

      const bounds = this.pageFlip.getBoundsRect();
      const corner = calc.getCorner ? calc.getCorner() : 'top';
      const y = corner === 'bottom' ? bounds.height : 0;
      const target = {
        x: shouldComplete ? -bounds.pageWidth : bounds.pageWidth,
        y
      };

      controller.animateFlippingTo(calc.getPosition(), target, shouldComplete, true);
    }

    getTouchDragAnchor(direction, y) {
      const bounds = this.pageFlip.getBoundsRect();
      const leftEdge = bounds.left;
      const rightEdge = bounds.left + bounds.width;

      return {
        x: direction === 'next' ? rightEdge - 3 : leftEdge + 3,
        y: Math.min(bounds.top + bounds.height - 3, Math.max(bounds.top + 3, y))
      };
    }

    getTouchEdgeAction(x) {
      const bounds = this.pageFlip.getBoundsRect();
      const isPortrait = this.pageFlip.getOrientation && this.pageFlip.getOrientation() === 'portrait';
      const leftEdge = isPortrait ? bounds.left + bounds.pageWidth : bounds.left;
      const rightEdge = bounds.left + bounds.width;
      const visibleWidth = Math.max(1, rightEdge - leftEdge);
      const edgeSize = Math.max(44, Math.min(90, visibleWidth * 0.18));

      if (x <= leftEdge + edgeSize) return 'prev';
      if (x >= rightEdge - edgeSize) return 'next';

      return null;
    }

    isTouchFlipAvailable(direction) {
      if (!this.pageFlip) return false;

      const pageIndex = this.pageFlip.getCurrentPageIndex();
      const pageCount = this.pageFlip.getPageCount();

      return direction === 'next' ? pageIndex < pageCount - 1 : pageIndex > 0;
    }

    cancelTouchTracking() {
      this.cancelPageFlipTouch();
      this.viewer.classList.remove('is-touch-zooming');

      this.touchState = null;
    }

    cancelPageFlipTouch() {
      if (!this.pageFlip) return;

      this.pageFlip.isUserTouch = false;
      this.pageFlip.isUserMove = false;
    }

    isZoomed() {
      return this.zoom > 1;
    }

    stopTouchEvent(event, preventDefault) {
      event.stopImmediatePropagation();

      if (preventDefault && event.cancelable) {
        event.preventDefault();
      }
    }

    onToolbarClick(event) {
      const control = event.target.closest('[data-hym-action]');
      if (!control || !this.pageFlip) return;

      const action = control.dataset.hymAction;

      if (action === 'prev') {
        this.goToPrev();
      }

      if (action === 'next') {
        this.goToNext();
      }

      if (action === 'fullscreen' || action === 'toggle-fullscreen') {
        this.toggleFullscreen();
      }

      if (action === 'close-fullscreen') {
        this.closeFullscreen();
      }

      if (action === 'zoom-in') {
        this.setZoom(this.zoom + this.zoomStep);
      }

      if (action === 'zoom-out') {
        this.setZoom(this.zoom - this.zoomStep);
      }

      if (action === 'zoom-reset') {
        this.setZoom(1);
      }
    }

    goToNext() {
      if (!this.pageFlip) return;

      this.pageFlip.flipNext();
    }

    goToPrev() {
      if (!this.pageFlip) return;

      this.pageFlip.flipPrev();
    }

    toggleFullscreen() {
      if (!this.viewer || !document.fullscreenEnabled) return;

      if (document.fullscreenElement) {
        this.closeFullscreen();
        return;
      }

      this.viewer.requestFullscreen();
    }

    closeFullscreen() {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    }

    onFullscreenChange() {
      if (!this.pageFlip) return;

      const isFullscreen = document.fullscreenElement === this.viewer;
      this.viewer.classList.toggle('is-fullscreen', isFullscreen);
      this.updateFullscreenButton(isFullscreen);
      this.updateHeaderOffset();

      window.setTimeout(() => {
        this.pageFlip.update();
        this.renderAround(this.pageFlip.getCurrentPageIndex());
        this.scheduleMobileStageSize();
      }, 120);
    }

    updateFullscreenButton(isFullscreen) {
      const fullscreenButton = this.fullscreenToggle;

      if (!fullscreenButton) return;

      fullscreenButton.setAttribute(
        'aria-label',
        isFullscreen ? 'Cerrar pantalla completa' : 'Abrir pantalla completa'
      );
    }

    setZoom(nextZoom, options = {}) {
      const previousZoom = this.zoom;
      const focus = options.focus || this.getZoomFocus(previousZoom);
      const baseSize = this.getBookBaseSize(previousZoom);
      this.zoom = Math.min(this.maxZoom, Math.max(this.minZoom, Number(nextZoom.toFixed(2))));

      if (!this.bookWrap || previousZoom === this.zoom) {
        this.updateZoomControls();
        return;
      }

      this.bookWrap.style.width = '';
      if (this.book) {
        this.book.style.maxWidth = this.baseBookMaxWidth;
      }

      this.viewer.dataset.zoomed = this.zoom > 1 ? 'true' : 'false';
      this.viewer.style.setProperty('--hym-catalog-zoom', String(this.zoom));
      this.updateZoomLayout(baseSize);
      this.updateZoomControls();
      this.restoreZoomFocus(focus);

      if (this.zoomUpdateTimer) {
        window.clearTimeout(this.zoomUpdateTimer);
      }

      this.zoomUpdateTimer = window.setTimeout(() => {
        this.zoomUpdateTimer = null;

        if (!this.pageFlip) return;

        this.renderAround(this.pageFlip.getCurrentPageIndex());
        this.scheduleMobileStageSize();
        this.restoreZoomFocus(focus);

        if (this.zoom === 1 && this.stage) {
          this.stage.scrollLeft = 0;
          this.stage.scrollTop = 0;
        }
      }, options.interactive ? 140 : 80);
    }

    getBookBaseSize(currentZoom) {
      if (!this.book) {
        return { width: 0, height: 0 };
      }

      const rect = this.book.getBoundingClientRect();
      const scale = Math.max(1, currentZoom || 1);

      return {
        width: rect.width / scale,
        height: rect.height / scale
      };
    }

    updateZoomLayout(baseSize) {
      if (!this.bookWrap) return;

      if (this.zoom <= 1 || !baseSize.width || !baseSize.height) {
        this.bookWrap.style.paddingLeft = '';
        this.bookWrap.style.paddingRight = '';
        this.bookWrap.style.paddingBottom = '';
        this.bookWrap.style.minHeight = '';
        return;
      }

      const extraWidth = baseSize.width * (this.zoom - 1);
      const extraHeight = baseSize.height * (this.zoom - 1);

      this.bookWrap.style.paddingLeft = `${Math.ceil(extraWidth / 2)}px`;
      this.bookWrap.style.paddingRight = `${Math.ceil(extraWidth / 2)}px`;
      this.bookWrap.style.paddingBottom = `${Math.ceil(extraHeight)}px`;
      this.bookWrap.style.minHeight = `${Math.ceil(baseSize.height * this.zoom)}px`;
    }

    getZoomFocus(currentZoom) {
      if (!this.stage) return null;

      const viewportX = this.stage.clientWidth / 2;
      const viewportY = this.stage.clientHeight / 2;

      return {
        contentX: this.stage.scrollLeft + viewportX,
        contentY: this.stage.scrollTop + viewportY,
        viewportX,
        viewportY,
        zoom: currentZoom || this.zoom
      };
    }

    restoreZoomFocus(focus) {
      if (!this.stage || !focus) return;

      if (this.zoom <= 1) {
        this.stage.scrollLeft = 0;
        this.stage.scrollTop = 0;
        return;
      }

      const ratio = this.zoom / Math.max(1, focus.zoom || 1);

      this.stage.scrollLeft = Math.max(0, focus.contentX * ratio - focus.viewportX);
      this.stage.scrollTop = Math.max(0, focus.contentY * ratio - focus.viewportY);
    }

    updateZoomControls() {
      if (this.zoomLabel) {
        this.zoomLabel.textContent = `${Math.round(this.zoom * 100)}%`;
      }

      const zoomOut = this.querySelector('[data-hym-action="zoom-out"]');
      const zoomIn = this.querySelector('[data-hym-action="zoom-in"]');

      if (zoomOut) zoomOut.disabled = this.zoom <= this.minZoom;
      if (zoomIn) zoomIn.disabled = this.zoom >= this.maxZoom;
    }

    onViewportChange() {
      if (this.headerOffsetFrame) return;

      this.headerOffsetFrame = window.requestAnimationFrame(() => {
        this.headerOffsetFrame = null;
        this.updateHeaderOffset();
        this.updateMobileStageSize();
      });
    }

    scheduleMobileStageSize() {
      if (this.stageSizeFrame) return;

      this.stageSizeFrame = window.requestAnimationFrame(() => {
        this.stageSizeFrame = null;
        this.updateMobileStageSize();
      });
    }

    updateMobileStageSize() {
      if (!this.stage || !this.book || !this.viewer) return;

      const isMobile = window.innerWidth < 768;
      const isFullscreen = document.fullscreenElement === this.viewer;

      if (!isMobile || isFullscreen) {
        this.stage.style.minHeight = '';
        return;
      }

      const bookRect = this.book.getBoundingClientRect();
      const stageStyles = window.getComputedStyle(this.stage);
      const paddingTop = parseFloat(stageStyles.paddingTop) || 0;
      const paddingBottom = parseFloat(stageStyles.paddingBottom) || 0;
      const nextHeight = Math.ceil(bookRect.height + paddingTop + paddingBottom);

      if (nextHeight > 0) {
        this.stage.style.minHeight = `${nextHeight}px`;
      }
    }

    updateHeaderOffset() {
      if (!this.viewer) return;

      this.viewer.dataset.touchMode = this.shouldUseNaturalSwipe() ? 'true' : 'false';

      if (document.fullscreenElement === this.viewer) {
        this.viewer.style.setProperty('--hym-catalog-header-overlap', '0px');
        return;
      }

      const viewerTop = this.viewer.getBoundingClientRect().top;
      const headerBottom = this.getStickyHeaderBottom();
      const overlap = Math.max(0, Math.ceil(headerBottom - viewerTop));

      this.viewer.style.setProperty('--hym-catalog-header-overlap', `${overlap}px`);
    }

    getStickyHeaderBottom() {
      const selectors = [
        '.shopify-section-header-sticky',
        '.hdt-section-header',
        '.hdt-header-sticky-true.is-sticky',
        '.shopify-section-group-header-group',
        'hdt-sticky-header'
      ];
      let headerBottom = 0;

      document.querySelectorAll(selectors.join(',')).forEach((element) => {
        const rect = element.getBoundingClientRect();
        const style = window.getComputedStyle(element);
        const isVisible =
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          rect.height > 0 &&
          rect.bottom > 0 &&
          rect.top <= 1;
        const isStickyHeader =
          style.position === 'sticky' ||
          style.position === 'fixed' ||
          element.classList.contains('shopify-section-header-sticky') ||
          element.classList.contains('is-sticky') ||
          element.tagName.toLowerCase() === 'hdt-sticky-header';

        if (!isVisible || !isStickyHeader) return;

        headerBottom = Math.max(headerBottom, rect.bottom);
      });

      return Math.min(Math.max(0, headerBottom), window.innerHeight);
    }

    shouldUseNaturalSwipe() {
      return window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 768;
    }

    updateControls(pageIndex) {
      const total = this.pdf ? this.pdf.numPages : 0;
      const orientation = this.pageFlip ? this.pageFlip.getOrientation() : 'portrait';
      const isLandscapeSpread = orientation === 'landscape' && !this.showCover;
      const current = pageIndex + 1;
      const lastInSpread = Math.min(total, current + 1);
      const currentLabel =
        isLandscapeSpread && lastInSpread > current ? `${current}-${lastInSpread}` : String(current);

      if (this.currentPage) this.currentPage.textContent = currentLabel;
      if (this.totalPages) this.totalPages.textContent = String(total);

      const prev = this.toolbar.querySelector('[data-hym-action="prev"]');
      const next = this.toolbar.querySelector('[data-hym-action="next"]');
      const lastPageIndex = orientation === 'landscape' && !this.showCover ? Math.max(0, total - 2) : total - 1;

      if (prev) prev.disabled = pageIndex <= 0;
      if (next) next.disabled = pageIndex >= lastPageIndex;
      this.updateZoomControls();
    }

    renderInitialPages(pageIndex) {
      const targets = new Set([pageIndex]);
      const orientation = this.pageFlip ? this.pageFlip.getOrientation() : 'portrait';

      if (orientation === 'landscape' && !this.showCover && pageIndex + 1 < this.pdf.numPages) {
        targets.add(pageIndex + 1);
      }

      targets.forEach((index) => this.renderPage(index));

      window.setTimeout(() => {
        if (!this.pageFlip) return;

        this.renderAround(this.pageFlip.getCurrentPageIndex());
      }, 450);
    }

    renderAround(pageIndex) {
      const targets = new Set();
      const first = Math.max(0, pageIndex - 2);
      const last = Math.min(this.pdf.numPages - 1, pageIndex + 3);

      for (let index = first; index <= last; index += 1) {
        targets.add(index);
      }

      targets.forEach((index) => this.renderPage(index));
      this.releaseFarPages(pageIndex, 5);
    }

    async renderPage(index) {
      if (this.renderedPages.has(index) || this.renderingPages.has(index)) {
        return this.renderingPages.get(index);
      }

      const promise = this.pdf
        .getPage(index + 1)
        .then(async (page) => {
          const viewport = page.getViewport({ scale: this.renderScale });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d', { alpha: false });

          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.setAttribute('aria-label', `Pagina ${index + 1}`);

          await page.render({ canvasContext: context, viewport }).promise;

          const pageElement = this.pageElements[index];
          pageElement.innerHTML = '';
          pageElement.appendChild(canvas);
          pageElement.dataset.rendered = 'true';
          this.renderedPages.add(index);
          this.scheduleMobileStageSize();
        })
        .catch((error) => {
          const pageElement = this.pageElements[index];
          pageElement.innerHTML = '';
          pageElement.appendChild(this.createPlaceholder(index + 1, true));
          throw error;
        })
        .finally(() => {
          this.renderingPages.delete(index);
        });

      this.renderingPages.set(index, promise);
      return promise;
    }

    releaseFarPages(pageIndex, keepDistance) {
      this.renderedPages.forEach((index) => {
        if (Math.abs(index - pageIndex) <= keepDistance) return;

        const pageElement = this.pageElements[index];
        pageElement.innerHTML = '';
        pageElement.appendChild(this.createPlaceholder(index + 1));
        pageElement.dataset.rendered = 'false';
        this.renderedPages.delete(index);
      });
    }

    createPlaceholder(pageNumber, hasError) {
      const placeholder = document.createElement('div');
      placeholder.className = 'hym-catalog-page-placeholder';
      placeholder.textContent = hasError ? 'No disponible' : `Pagina ${pageNumber}`;
      return placeholder;
    }

    showEmpty() {
      this.show(this.empty);
      this.hide(this.loading);
      this.hide(this.bookWrap);
      this.hide(this.toolbar);
      this.hide(this.fullscreenToggle);
      this.hide(this.error);
    }

    showError(error) {
      this.hide(this.loading);
      this.hide(this.bookWrap);
      this.hide(this.toolbar);
      this.hide(this.fullscreenToggle);
      this.show(this.error);
      this.setAttribute('data-error', error.message || 'Unknown error');
    }

    show(element) {
      if (element) element.hidden = false;
    }

    hide(element) {
      if (element) element.hidden = true;
    }
  }

  window.customElements.define(elementName, HymPdfFlipbook);
})();
